
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService, ResponseObject } from './auth.service';
import { ServiceBase } from './service.base';
import { SelectItem } from 'primeng/api';
import { Artist } from './artists.service';
import { InviteCommentRequest } from '../components/artist-components/connected-galleries/connected-galleries.component';
import { InviteAcceptanceCommentRequest } from '../components/connection-request/connection-request.component';
import { ArtistProductsSummaryRequest, ConsignedProductsRequest } from './artistproducts.service';

@Injectable({
  providedIn: 'root'
})
export class ConnectionsService extends ServiceBase {

  constructor(private http: HttpClient, authService: AuthService) {
    super(authService);
  }


  public getConnectedGalleries(artistId: string | null = null, includeInvited: boolean | null = null, filters: ArtistProductsSummaryRequest = {}): Observable<ConnectedGallery[]> {
    const api: string = '/Connections';
    const method: string = '/artists/' + artistId + '/connected-galleries/' + includeInvited;
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ConnectedGallery[]>(url, filters, this.RequestHeaders());
  }

  // Get connected artists for a specific gallery
  public getConnectedArtists(galleryId: string | null = null, includeInvited: boolean | null = null, filters: ArtistProductsSummaryRequest = {}): Observable<Artist[]> {
    const api: string = '/Connections';
    const method: string = '/galleries/' + galleryId + '/connected-artists/' + includeInvited;
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<Artist[]>(url, filters, this.RequestHeaders());
  }

  // Get available galleries for a specific artist
  public getAvailableGalleries(artistId: string): Observable<SelectItem[]> {
    const api: string = '/Connections';
    const method: string = '/artists/' + artistId + '/available-galleries';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.get<SelectItem[]>(url, this.RequestHeaders());
  }

  // Get available artists for a specific gallery
  public getAvailableArtists(galleryId: string | null): Observable<SelectItem[]> {
    const api: string = '/Connections';
    const method: string = '/gallery/' + galleryId + '/available-artists';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.get<SelectItem[]>(url, this.RequestHeaders());
  }

  // Send invite from a specific artist to a specific gallery
  public sendInvite(artistId: string | null, galleryId: string | null, formData: InviteCommentRequest | null = null): Observable<ResponseObject> {
    const api: string = '/Connections';
    const method: string = '/artists/' + artistId + '/galleries/' + galleryId + '/send-invite';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, formData, this.RequestHeaders());
  }

  public saveConnectedGallerySettings(galleryId: string, settingData: ConnectedGallerySettingRequest): Observable<ResponseObject> {
    const api: string = '/Connections';
    const method: string = '/galleries/' + galleryId + '/save-setting';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, settingData, this.RequestHeaders());
  }

  public saveConnectedArtistSettings(artistId: string, settingData: ConnectedArtistSettingRequest): Observable<ResponseObject> {
    const api: string = '/Connections';
    const method: string = '/artists/' + artistId + '/save-setting';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, settingData, this.RequestHeaders());
  }

  public getArtistNotes(artistId: string): Observable<string> {
    const api: string = '/Connections';
    const method: string = '/connected-artists/' + artistId + '/artist-notes';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.get<string>(url, this.RequestHeaders());
  }

  public inviteExternalUser(inviteExternalUserData: InviteExternalUserData): Observable<ResponseObject> {
    const api: string = '/ExternalInvitation';
    const method: string = '/inviteExternalUser';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, inviteExternalUserData, this.RequestHeaders());
  }

  public acceptConnectionRequest(connectionId: string, commentData: InviteAcceptanceCommentRequest | null = null): Observable<ResponseObject> {
    const api: string = '/connections';
    const method: string = '/' + connectionId + '/accept';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, commentData, this.RequestHeaders());
  }

  public getConnectionDetail(connectionId: string): Observable<ConnectionDetail> {
    const api: string = '/connections';
    const method: string = '/' + 'connection-detail/';
    const url: string = environment.BASE_API_PATH + api + method + connectionId;
    return this.http.get<ConnectionDetail>(url, this.RequestHeaders());
  }

  public deleteConnectedGallery(galleryId: string): Observable<ResponseObject> {
    const api: string = '/connections';
    const method: string = '/' + galleryId + '/gallery';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.delete<ResponseObject>(url, this.RequestHeaders());
  }

  public deleteConnectedArtist(artistId: string): Observable<ResponseObject> {
    const api: string = '/connections';
    const method: string = '/' + artistId + '/artist';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.delete<ResponseObject>(url, this.RequestHeaders());
  }

  public termsAcceptance(galleryId: string): Observable<ResponseObject> {
    const api: string = '/connections';
    const method: string = '/' + galleryId + '/terms-acceptance';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.get<ResponseObject>(url, this.RequestHeaders());
  }

  public acceptanceTerms(galleryId: string): Observable<ResponseObject> {
    const api: string = '/connections';
    const method: string = '/' + galleryId + '/terms-acceptance';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, {}, this.RequestHeaders());
  }

  public rejectInvitation(artistId: string | null, galleryId: string | null, formData: InviteAcceptanceCommentRequest | null = null): Observable<ResponseObject> {
    const api: string = '/Connections';
    const method: string = '/artists/' + artistId + '/galleries/' + galleryId + '/reject';
    const url: string = environment.BASE_API_PATH + api + method;
    return this.http.post<ResponseObject>(url, formData, this.RequestHeaders());
  }
}

// interfaces for terms acceptance
export interface TermsAcceptanceStatus {
  IsAccepted: boolean;
  CurrentVersion: number;
  ArtistAcceptedVersion: number;
}

export class ConnectedGallery {
  Id!: string;
  Name!: string;
  UserName!: string;
  Email!: string;
  PhoneNumber!: string;
  GalleryImgUrl!: string;
  IsInviteAccepted!: boolean;
  InvitedOn!: Date;
  InviteAcceptedOn!: Date;
  IsInitiatedByArtist!: boolean;
  TermsAcceptanceStatus!: TermsAcceptanceStatus | null;
  AllowAvalableInventory?: boolean | undefined;
  GalleryNotes?: string;
  Website?: string;
  FacebookLink?: string;
  InstagramLink?: string;
  LinkedinLink?: string;
  ConnectionId?: string;
  // Runtime-only fields returned by the API (not part of the core invite shape):
  TermsVersion?: { IsAccepted: boolean };
  GallerySoldAndInventoryData?: {
    NoOfSoldArtworks?: number;
    TotalSoldPriceOfSoldItems?: number;
    NoOfAvailableArtworks?: number;
    TotalRetailPriceOfAvailableItems?: number;
  };
}

export class Connection {
  Id!: string;
  Name!: string;
  Email!: string;
  PhoneNumber!: string;
  Website!: string;
  GallerySaleType!: string;
  GalleryType!: string;
  GstId!: string;
  ABN!: string;
  AddGST!: boolean;
  CommissionPercentage!: number;
  Department!: string;
  GeneralDetails!: string;
  DeliveryInstructions!: string;
  Terms!: string;
  CreatedDate!: Date;
}

export class InviteExternalUserData {
  Name!: string;
  Email!: string;
  InviteExternalComment!: string;
}


export class ConnectedGallerySettingRequest {
  AllowAvailableInventory?: boolean;
  GalleryNotes?: string;
}
export class ConnectedArtistSettingRequest {
  ArtistNotes?: string | null;
}

export class ConnectionDetail {
  GalleryId!: string;
  ArtistId?: string;
  IsInitiatedByArtist!: boolean;
  RequestSentDate?: Date;
  IsAccepted?: boolean;
  SenderName!: string;
  SenderImageUrl?: string;
  SenderBusinessImageUrl?: string;
  SenderPhoneNumber?: string;
  SenderEmail?: string;
  SenderAddressLine1?: string;
  SenderAddressLine2?: string;
  SenderWebsite?: string;
  SenderFacebookLink?: string;
  SenderBusinessFacebookLink?: string;
  SenderLinkedinLink?: string;
  SenderBusinessLinkedinLink?: string;
  SenderInstagramLink?: string;
  SenderBusinessInstagramLink?: string;
  SenderAboutMe?: string;
  SenderResume?: string;
  SenderNewProductsAdded: SenderConnectionProduct[] = [];
  SenderRecentSales: SenderConnectionProduct[] = [];
  ArtistKeyStats?: ArtistConnectionRequestKeyStats;
}

export interface SenderConnectionProduct {
  ArtistId: string;
  AcceptedDate: Date;
  AcceptedDaysAgo: Date;
  ProductName: string;
  ArtistProductId: string;
  ArtTypeName: string;
  ArtistRevenue: string;
  ProductImageUrl: string;
  GalleryName: string;
  SoldPrice: number;
  IsSold: boolean;
}

export class ArtistConnectionRequestKeyStats {
  ArtistArtTypes: string[] = [];
  NoOfConsignmentsDispatched?: number;
  NoOfDispatchedProducts?: number;
  AverageRetailPrice?: number;
  AverageTurnoverRate?: number;
  NoOfSoldProducts?: number;
  AverageSalesValuePerProduct?: number;
  TotalSalesValue?: number;
}
